import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import type { Router } from 'vue-router';

export interface ITelemetryService {
  init(): void;
  isEnabled(): boolean;
  setAuthenticatedUser(email: string): void;
  clearAuthenticatedUser(): void;
  trackPageView(name?: string, uri?: string): void;
  trackRouter(router: Router): void;
  trackException(error: unknown, properties?: Record<string, string>): void;
  trackEvent(name: string, properties?: Record<string, string>): void;
}

class TelemetryServiceClass implements ITelemetryService {
  private static instance: TelemetryServiceClass;
  private appInsights: ApplicationInsights | null = null;
  
  private constructor() { }
  
  public static getInstance(): TelemetryServiceClass {
    if (!TelemetryServiceClass.instance) {
      TelemetryServiceClass.instance = new TelemetryServiceClass();
    }
    return TelemetryServiceClass.instance;
  }

  public init(): void {
    const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
    const role = import.meta.env.VITE_APPINSIGHTS_ROLE;
    const mode = (import.meta.env.VITE_APPINSIGHTS_MODE || 'Auto').toLowerCase();

    if (!connectionString) {
      return;
    }

    if (mode === 'off') {
      return;
    }

    if (mode !== 'on' && import.meta.env.DEV) {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false,
        enableCorsCorrelation: false,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false,
      }
    });

    this.appInsights.loadAppInsights();

    // Strip Authorization and Cookie from all telemetry envelopes — defense in depth
    // against any SDK path (dependency tracking, exception auto-capture) that might
    // serialize request config or headers.
    this.appInsights.addTelemetryInitializer((envelope) => {
      const baseData = envelope.baseData;
      const props = baseData && typeof baseData === 'object'
        ? (baseData as { properties?: unknown }).properties
        : undefined;
      if (props && typeof props === 'object') {
        const properties = props as Record<string, unknown>;
        for (const key of Object.keys(properties)) {
          if (/authorization|cookie/i.test(key)) {
            delete properties[key];
          }
        }
      }
    });

    if (role) {
      this.appInsights.addTelemetryInitializer((envelope) => {
        envelope.tags = envelope.tags || [];
        envelope.tags['ai.cloud.role'] = role;
      });
    }

    this.appInsights.trackPageView();
  }

  public isEnabled(): boolean {
    return this.appInsights !== null;
  }

  public setAuthenticatedUser(email: string): void {
    if (!this.appInsights) return;
    this.appInsights.setAuthenticatedUserContext(email, undefined, true);
  }

  public clearAuthenticatedUser(): void {
    if (!this.appInsights) return;
    this.appInsights.clearAuthenticatedUserContext();
  }

  public trackPageView(name?: string, uri?: string): void {
    if (!this.appInsights) return;
    this.appInsights.trackPageView({ name, uri });
  }

  public trackRouter(router: Router): void {
    router.afterEach((to) => {
      this.trackPageView(to.name?.toString() ?? to.path, to.fullPath);
    });
  }

  public trackException(error: unknown, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    const exception = error instanceof Error ? error : new Error(String(error));
    this.appInsights.trackException({ exception, properties });
  }

  public trackEvent(name: string, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    this.appInsights.trackEvent({ name }, properties);
  }
}

export const TelemetryService: ITelemetryService = TelemetryServiceClass.getInstance();
